import { Button, DialogFooter } from "frontend-components";
import { useTranslation } from "react-i18next";
import { useImperativeHandle, useRef, type Ref } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { RRule } from "rrule";

import {
    useTaskFormSetup,
    VALIDATION_LIMITS,
} from "@dashboard/features/Dashboard/AnnualCycle/TaskForm/formHooks";
import { type TaskWithOccurrence } from "@dashboard/utils/taskWithOccurrence";

import { RelatedLinksSection } from "./RelatedLinksSection";
import type { TaskLinksApi } from "./useTaskLinks";

export interface TaskFormBodyHandle {
    isDirty: () => boolean;
    markDirty: () => void;
}

export function TaskFormBody({
    task,
    ref,
    links,
    onRequestRemoveLink,
    onCancel,
    onSaved,
    onRequestDelete,
}: {
    task: TaskWithOccurrence | null;
    ref?: Ref<TaskFormBodyHandle>;
    links: TaskLinksApi;
    onRequestRemoveLink: (id: number) => void;
    onCancel: () => void;
    onSaved: (taskId?: number) => void;
    onRequestDelete?: () => void;
}) {
    const { formOpts, mapTaskToForm, useTaskForm, handleTaskSubmit, submitError } =
        useTaskFormSetup();
    const { t } = useTranslation();
    const isEditMode = !!task;

    const rule = task?.rrule ? RRule.fromString(task.rrule) : undefined;
    const isRecurring = rule ? rule.all((_, i) => i < 2).length > 1 : false;
    const isDone = task?.occurrence?.status === "DONE";
    const isDateDisabled = isRecurring || isDone;

    const persistedTaskIdRef = useRef<number | undefined>(task?.id);
    const linksDirtyRef = useRef(false);

    const form = useTaskForm({
        defaultValues: task ? mapTaskToForm(task) : formOpts.defaultValues,
        validators: formOpts.validators,
        onSubmit: async ({ value }) => {
            links.clearSubmitError();

            const existingId = persistedTaskIdRef.current;
            let savedTaskId: number | undefined;

            if (existingId) {
                const result = await handleTaskSubmit(value, existingId, isRecurring);
                if (!result.success) return;
                savedTaskId = existingId;
            } else {
                const result = await handleTaskSubmit(value, undefined, isRecurring);
                if (!result.success) return;
                savedTaskId = result.taskId;
                if (savedTaskId !== undefined) persistedTaskIdRef.current = savedTaskId;
            }

            if (!isEditMode && savedTaskId !== undefined && links.hasUnposted()) {
                const ok = await links.postPending(savedTaskId);
                if (!ok) return;
            }

            linksDirtyRef.current = false;
            links.reset();
            onSaved(savedTaskId);
        },
    });

    useImperativeHandle(ref, () => ({
        isDirty: () => form.state.isDirty || linksDirtyRef.current,
        markDirty: () => {
            linksDirtyRef.current = true;
        },
    }));

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
            className="w-full flex flex-col gap-4 text-compendia-foreground overflow-hidden"
        >
            <form.AppField
                name="title"
                children={(field) => (
                    <field.Input
                        label={t("common.title")}
                        maxLength={VALIDATION_LIMITS.TITLE_MAX_LENGTH}
                        showCharCount={true}
                        placeholder={t("common.whatIsThisActivityAbout") + "?"}
                    />
                )}
            />

            <form.AppField
                name="description"
                children={(field) => (
                    <field.TextArea
                        label={t("common.description")}
                        maxLength={VALIDATION_LIMITS.DESCRIPTION_MAX_LENGTH}
                        showCharCount={true}
                        placeholder={t("common.briefDescriptionOfTheActivity") + "?"}
                    />
                )}
            />

            <div className="w-full sm:w-1/2">
                <form.AppField
                    name="responsible"
                    children={(field) => (
                        <field.UserSelect
                            label={t("common.responsible")}
                            placeholder={t("common.whoIsResponsibleForThisActivity") + "?"}
                        />
                    )}
                />
            </div>

            <div className="flex flex-col gap-1">
                <span className="text-label font-medium uppercase tracking-wider text-text-secondary">
                    {t("common.dueDate")}
                </span>
                <div className="flex gap-2 items-center">
                    <form.AppField
                        name="endDate"
                        children={(field) => <field.DateInput disabled={isDateDisabled} />}
                    />
                </div>
            </div>

            <RelatedLinksSection
                links={links.items}
                onAdd={links.add}
                onRequestRemove={onRequestRemoveLink}
                mutationError={links.mutationError}
                disabled={links.isMutating}
            />

            {submitError && (
                <div className="text-chameleon-pink text-sm p-2 bg-chameleon-pink/10 rounded-none">
                    {t("common.errorSavingActivity")}: {submitError.message}
                </div>
            )}

            {links.submitError && (
                <div className="text-chameleon-pink text-sm p-2 bg-chameleon-pink/10 rounded-none">
                    {links.submitError}
                </div>
            )}

            <DialogFooter>
                <div className="flex items-center justify-between gap-2 w-full">
                    {isEditMode && onRequestDelete ? (
                        <Button
                            type="button"
                            aria-label={t("common.deleteActivity")}
                            className="rounded-pill border border-compendia-foreground bg-transparent text-compendia-foreground hover:border-chameleon-pink hover:text-chameleon-pink hover:bg-transparent px-3 py-2 text-sm font-semibold sm:px-5"
                            onClick={onRequestDelete}
                        >
                            <FontAwesomeIcon icon={faTrashCan} className="sm:mr-2" />
                            <span className="hidden sm:inline">{t("common.deleteActivity")}</span>
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2">
                        <form.Subscribe selector={(state) => [state.isSubmitting, state.canSubmit]}>
                            {([isSubmitting, canSubmit]) => (
                                <>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={isSubmitting}
                                        onClick={onCancel}
                                    >
                                        {t("common.cancel")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="submit"
                                        size="submit"
                                        disabled={!canSubmit || links.isPostingLinks}
                                    >
                                        {isEditMode ? t("common.save") : t("common.create")}
                                    </Button>
                                </>
                            )}
                        </form.Subscribe>
                    </div>
                </div>
            </DialogFooter>
        </form>
    );
}
